(function () {
  const moneyFormat = window?.theme?.moneyFormat || window?.Shopify?.currency?.active_format || '${{amount}}';

  const formatMoney = (value) => {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(value, moneyFormat);
    }
    const amount = (Number(value) || 0) / 100;
    return amount.toLocaleString(undefined, {
      style: 'currency',
      currency: window?.Shopify?.currency?.active || 'USD'
    });
  };

  class ProductAddonsBlock {
    constructor(element) {
      this.element = element;
      this.maxQty = parseInt(element.dataset.maxQty || '10', 10);
      this.defaultOpen = element.dataset.openByDefault === 'true';
      this.items = Array.from(element.querySelectorAll('[data-addon-item]'));
      this.initializeCollapsible();
      this.items.forEach((item) => this.initializeItem(item));
    }

    initializeCollapsible() {
      const content = this.element.querySelector('[data-addon-collapsible-content]');
      const toggle = this.element.querySelector('[data-addon-collapsible-toggle]');
      if (!content) return;

      // If toggle doesn't exist, content should always be visible
      if (!toggle) {
        content.removeAttribute('hidden');
        return;
      }

      const isOpen = this.defaultOpen;
      content.hidden = !isOpen;
      toggle.setAttribute('aria-expanded', String(isOpen));

      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        content.hidden = expanded;
      });
    }

    initializeItem(item) {
      const checkbox = item.querySelector('[data-addon-checkbox]');
      const variantSelect = item.querySelector('[data-addon-variant-select]');
      const quantityInput = item.querySelector('[data-addon-quantity]');
      const decreaseBtn = item.querySelector('[data-quantity-button="decrease"]');
      const increaseBtn = item.querySelector('[data-quantity-button="increase"]');
      const selectedOptionLabel = item.querySelector('[data-selected-option-label]');
      const priceNode = item.querySelector('[data-addon-price]');
      const comparePriceNode = item.querySelector('[data-addon-compare-price]');
      const compareWrapper = item.querySelector('[data-addon-compare-wrapper]');
      const imageNode = item.querySelector('[data-addon-image]');
      const variantScript = item.querySelector('[data-addon-variants]');

      let variants = [];
      if (variantScript) {
        try {
          const jsonText = variantScript.textContent.trim();
          if (jsonText) {
            variants = JSON.parse(jsonText);
            if (!Array.isArray(variants)) {
              variants = [];
            }
          }
        } catch (error) {
          console.error('Unable to parse add-on variant data', error);
          variants = [];
        }
      }

      const updateVariantUi = (variantId) => {
        if (!variants.length) return;
        const variant = variants.find((entry) => String(entry.id) === String(variantId)) || variants[0];
        if (!variant) return;

        // Update selected option label
        if (selectedOptionLabel) {
          selectedOptionLabel.textContent = variant.title;
        }

        // Update price
        if (priceNode) {
          priceNode.textContent = formatMoney(variant.price);
        }

        // Update compare at price
        if (comparePriceNode && compareWrapper) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            comparePriceNode.textContent = formatMoney(variant.compare_at_price);
            compareWrapper.classList.remove('hidden');
          } else {
            compareWrapper.classList.add('hidden');
          }
        }

        // Update image if variant has specific image
        if (imageNode && variant.image) {
          imageNode.src = variant.image;
          imageNode.style.display = 'block';
          const placeholder = item.querySelector('[data-addon-image-placeholder]');
          if (placeholder) {
            placeholder.style.display = 'none';
          }
        }

        // Update item data attribute with selected variant
        item.dataset.selectedVariant = String(variant.id);

        // Update variant select value if it exists
        if (variantSelect && variantSelect.value !== String(variant.id)) {
          variantSelect.value = String(variant.id);
        }

        // Store variant availability on item for setControlsState to use
        item.dataset.variantAvailable = String(variant.available !== false);
      };

      const setControlsState = (enabled) => {
        // Check if variant is available (default to true if not set)
        const variantAvailable = item.dataset.variantAvailable !== 'false';
        // Controls should be enabled if variant is available
        // If checkbox exists, it also needs to be checked, otherwise always enabled (quantity-based logic)
        const shouldEnable = variantAvailable && (checkbox ? enabled : true);
        
        [quantityInput, decreaseBtn, increaseBtn].forEach((control) => {
          if (!control) return;
          control.disabled = !shouldEnable;
        });
        // Variant select should always be enabled (options are disabled individually)
        if (variantSelect) {
          variantSelect.disabled = false;
        }
        // Item is active if quantity > 0 (for visual feedback)
        const currentQty = quantityInput ? parseInt(quantityInput.value || '0', 10) : 0;
        item.classList.toggle('is-active', currentQty > 0 && shouldEnable);
      };

      if (checkbox) {
        // Initialize checkbox state based on initial quantity
        const initialQty = quantityInput ? parseInt(quantityInput.value || '0', 10) : 0;
        checkbox.checked = initialQty > 0;
        
        checkbox.addEventListener('change', () => setControlsState(checkbox.checked));
        // Initialize state based on checkbox
        setControlsState(checkbox.checked);
      } else {
        // If no checkbox, controls are always enabled (quantity-based logic)
        setControlsState(true);
        // Initialize item active state based on quantity
        const initialQty = quantityInput ? parseInt(quantityInput.value || '0', 10) : 0;
        item.classList.toggle('is-active', initialQty > 0);
      }

      if (variantSelect) {
        // Initialize with current selected value
        const initialVariantId = variantSelect.value || item.dataset.defaultVariant;
        if (initialVariantId) {
          updateVariantUi(initialVariantId);
        }

        // Handle variant change
        variantSelect.addEventListener('change', (event) => {
          const selectedVariantId = event.target.value;
          updateVariantUi(selectedVariantId);
          // Update controls state after variant change (to respect availability)
          if (checkbox) {
            setControlsState(checkbox.checked);
          } else {
            setControlsState(true);
          }
        });
      } else if (item.dataset.defaultVariant) {
        // No variant selector, use default variant
        // Try to find variant in variants array to get availability
        if (variants.length) {
          const defaultVariant = variants.find(v => String(v.id) === String(item.dataset.defaultVariant));
          if (defaultVariant) {
            item.dataset.variantAvailable = String(defaultVariant.available !== false);
          } else {
            item.dataset.variantAvailable = 'true'; // Default to available if not found
          }
        } else {
          item.dataset.variantAvailable = 'true'; // Default to available if no variant data
        }
        updateVariantUi(item.dataset.defaultVariant);
      }

      if (quantityInput) {
        const sanitizeQuantity = (value) => {
          const parsed = parseInt(value || '0', 10);
          const upperBound = parseInt(quantityInput.getAttribute('max') || this.maxQty || '10', 10);
          if (Number.isNaN(parsed) || parsed < 0) return 0;
          if (parsed > upperBound) return upperBound;
          return parsed;
        };

        const updateCheckboxFromQuantity = () => {
          if (checkbox) {
            const qty = parseInt(quantityInput.value || '0', 10);
            checkbox.checked = qty > 0;
            // Update controls state - but keep them enabled so user can change quantity
            setControlsState(true);
          } else {
            // Update item active state based on quantity
            const qty = parseInt(quantityInput.value || '0', 10);
            item.classList.toggle('is-active', qty > 0);
          }
        };

        quantityInput.value = sanitizeQuantity(quantityInput.value);
        updateCheckboxFromQuantity();
        
        quantityInput.addEventListener('change', () => {
          quantityInput.value = sanitizeQuantity(quantityInput.value);
          updateCheckboxFromQuantity();
        });

        if (decreaseBtn) {
          decreaseBtn.addEventListener('click', (event) => {
            event.preventDefault();
            if (quantityInput.disabled) return;
            const newValue = sanitizeQuantity(parseInt(quantityInput.value, 10) - 1);
            quantityInput.value = newValue;
            updateCheckboxFromQuantity();
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }

        if (increaseBtn) {
          increaseBtn.addEventListener('click', (event) => {
            event.preventDefault();
            if (quantityInput.disabled) return;
            const newValue = sanitizeQuantity(parseInt(quantityInput.value, 10) + 1);
            quantityInput.value = newValue;
            updateCheckboxFromQuantity();
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }
      }
    }
  }

  function initProductAddons() {
    document.querySelectorAll('[data-product-addons]').forEach((element) => {
      if (element.__productAddonsInstance) return;
      element.__productAddonsInstance = new ProductAddonsBlock(element);
    });
  }

  document.addEventListener('DOMContentLoaded', initProductAddons);
  document.addEventListener('shopify:section:load', initProductAddons);
})();

